import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import { CircleNotch, Warning, MapPin, Clock, CheckCircle, FileText } from '@phosphor-icons/react';

export default function WorkOrderPublicPage() {
  const { woId } = useParams();
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/work-order/${woId}`);
        setWo(res.data);
      } catch {
        setError('Orden de trabajo no encontrada');
      } finally {
        setLoading(false);
      }
    })();
  }, [woId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <CircleNotch size={48} className="animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !wo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <Warning size={48} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Error</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const pdfUrl = `${API_URL}/api/public/work-order/${woId}/pdf`;
  const addrFull = [wo.project_address, wo.project_city, wo.project_state, wo.project_zip].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50" data-testid="work-order-public-page">
      <div className="bg-[#7C3AED] text-white py-6 px-4 text-center">
        <h1 className="text-2xl font-bold">Orden de Trabajo</h1>
        <p className="text-violet-200 text-sm">IQES LLC - Low Voltage Solutions</p>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-bold text-slate-900">OT #{wo.work_order_number}</h2>
              <p className="text-sm text-slate-600">{wo.client_name}</p>
              {wo.quote_number && (
                <p className="text-xs text-slate-500 mt-1">Cotizacion Ref: {wo.quote_number}</p>
              )}
            </div>
            <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold uppercase">
              {wo.status}
            </span>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
            <Clock size={20} /> Programacion
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-blue-600 text-xs">Fecha</p>
              <p className="font-semibold text-slate-900">{wo.scheduled_date || 'Por confirmar'}</p>
            </div>
            <div>
              <p className="text-blue-600 text-xs">Hora</p>
              <p className="font-semibold text-slate-900">{wo.scheduled_time || '09:00 AM'}</p>
            </div>
            <div>
              <p className="text-blue-600 text-xs">Prioridad</p>
              <p className="font-semibold text-slate-900 uppercase">{wo.priority}</p>
            </div>
            <div>
              <p className="text-blue-600 text-xs">Estado</p>
              <p className="font-semibold text-slate-900 uppercase">{wo.status}</p>
            </div>
          </div>
          {addrFull && (
            <div className="mt-3 pt-3 border-t border-blue-200 flex items-start gap-2 text-sm">
              <MapPin size={18} className="text-blue-700 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-600 text-xs">Direccion del proyecto</p>
                <p className="text-slate-900">{addrFull}</p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3">Alcance del Trabajo</h3>
          {wo.checklist && wo.checklist.length > 0 ? (
            <ul className="space-y-2">
              {wo.checklist.map((it, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" weight="regular" />
                  <span>{it.description}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Alcance segun cotizacion firmada.</p>
          )}
          {wo.notes && (
            <div className="mt-4 pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-500 mb-1">Notas:</p>
              <p className="text-sm text-slate-700">{wo.notes}</p>
            </div>
          )}
        </div>

        <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-4">
          <p className="text-sm font-bold text-red-800 mb-1">AVISO IMPORTANTE - SOLO MANO DE OBRA</p>
          <p className="text-xs text-red-700">
            Este trabajo cubre <strong>UNICAMENTE mano de obra</strong>. NO incluye materiales, equipos,
            permisos, inspecciones, impuestos ni tarifas de servicios publicos. Cualquier cambio en el
            alcance sera cotizado por separado.
          </p>
        </div>

        <div className="text-center">
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-xl font-semibold text-sm hover:bg-violet-700 transition-colors"
            data-testid="download-wo-pdf-btn"
          >
            <FileText size={20} /> Descargar PDF
          </a>
        </div>

        <div className="text-center py-4 mt-2">
          <p className="text-xs font-bold text-slate-700">IQES LLC</p>
          <p className="text-xs text-slate-600">Innovation | Quality | Engineering | Services</p>
          <p className="text-xs text-slate-500 italic mt-1">
            &ldquo;Building Strategic Partnerships Through Innovation, Quality, Engineering, and Service.&rdquo;
          </p>
          <p className="text-xs text-slate-500 mt-2">
            255 S Orange Avenue, Suite 104 #1905, Orlando, FL 32801 | 786-436-7307
          </p>
          <p className="text-xs text-slate-500">info@iqeslowvoltage.com | www.iqeslowvoltage.com</p>
        </div>
      </div>
    </div>
  );
}
